import React, { useMemo, useState } from 'react';

import Box     from '../../widget/windows/Box';
import Trigger from '../../widget/actuators/Trigger';
import Field         from '../../widget/inputs/Field';
import FieldCalendar from '../../widget/inputs/FieldCalendar';
import TextArea      from '../../widget/inputs/TextArea';
import { convertStringToDate } from '../../../dataTypes/timeManagment/utils';

import {
  sendCommunityMessage,
  fetchSentCommunityMessages,
  fetchCommunityMessageReads,
  retractCommunityMessage,
  SentCommunityMessage,
  CommunityMessageReadStatus,
} from '../../../backendServices/communityManagment/api';
import { ROUGE_PRIORITAIRE     } from './CommunityMessageManager';
import { getAdmins            } from '../../../backendServices/adminManagment/api';
import { Admin                 } from '../../../dataTypes/adminManagment/types';

/**
 * Le bouton « Message » du Superadmin, et la fenêtre qui va avec.
 *
 * ⚠️ Ce bouton est le SEUL verrou : `^/api/` répond sans jeton sur cette API,
 * quiconque sait former la requête peut donc écrire à la communauté. Choix
 * assumé au 31/08/2026 — c'est la règle de toute l'application aujourd'hui, pas
 * une faiblesse propre à cet écran — mais c'est une dette, et c'est ici et dans
 * CommunityMessageCreation qu'elle se solderait.
 *
 * L'échéance est facultative. Sans elle, le message reste jusqu'à son retrait :
 * c'est le cas courant d'une note de service qu'on veut voir lue par ceux qui se
 * connecteront demain.
 */

const LONGUEUR_MAX = 1000;
const LONGUEUR_TITRE_MAX = 200;

const formatDateEnvoi = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('fr-FR', {
    day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
  });
};

interface CommunityMessageComposerProps {
  authorId   : number | null;
  authorName : string;
  /** Rejoue le relevé, pour que l'auteur voie tout de suite ce qu'il vient d'envoyer. */
  onSent     : () => void;
}

type Audience = 'all' | 'chosen';
type Vue      = 'ecrire' | 'envoyes';

// Rouge très pâle → vert : le badge se lit d'un coup d'œil, sans avoir à lire
// le chiffre, quand une jauge intermédiaire se présente.
const couleurJauge = (ratio: number): string => {
  const depart = [156, 163, 175]; // gris (#9ca3af) : personne n'a encore lu
  const arrivee = [22, 163, 74];  // vert (#16a34a) : tout le monde a lu
  const [r, g, b] = depart.map((c, i) => Math.round(c + (arrivee[i] - c) * ratio));
  return `rgb(${r}, ${g}, ${b})`;
};

const CommunityMessageComposer = ({ authorId, authorName, onSent }: CommunityMessageComposerProps) => {
  const [open   , setOpen   ] = useState(false);
  const [vue    , setVue    ] = useState<Vue>('ecrire');
  const [titre  , setTitre  ] = useState('');
  const [texte  , setTexte  ] = useState('');
  const [echeance, setEcheance] = useState('');
  // « Prioritaire » : en rouge dans la régie, affiché aussi dans les apps Kleon
  // Studio, et permanent jusqu'au retrait — d'où l'échéance masquée.
  const [prioritaire, setPrioritaire] = useState(false);
  // Aussi sous le panneau de connexion de la régie (visible avant toute connexion).
  const [surConnexion, setSurConnexion] = useState(false);
  const [sending, setSending] = useState(false);
  const [erreur , setErreur ] = useState<string | null>(null);
  const [envoye , setEnvoye ] = useState(false);

  const [sentMessages   , setSentMessages   ] = useState<SentCommunityMessage[]>([]);
  const [sentLoading    , setSentLoading    ] = useState(false);
  const [sentErreur     , setSentErreur     ] = useState<string | null>(null);
  // Bloc compacté (titre + date) tant qu'on ne l'a pas ouvert.
  const [openSentId     , setOpenSentId     ] = useState<number | null>(null);
  // Sous-détail « qui a lu », dans le bloc déjà ouvert.
  const [expandedId     , setExpandedId     ] = useState<number | null>(null);
  const [readsById       , setReadsById       ] = useState<Record<number, CommunityMessageReadStatus[]>>({});
  const [readsLoadingId  , setReadsLoadingId  ] = useState<number | null>(null);
  const [retractingId    , setRetractingId    ] = useState<number | null>(null);

  const [audience       , setAudience       ] = useState<Audience>('all');
  const [creators        , setCreators        ] = useState<Admin[]>([]);
  const [creatorsLoading , setCreatorsLoading ] = useState(false);
  const [creatorsErreur  , setCreatorsErreur  ] = useState<string | null>(null);
  const [selectedIds     , setSelectedIds     ] = useState<Set<number>>(new Set());
  const [recherche        , setRecherche        ] = useState('');

  // On ne retire que l'expéditeur lui-même : il ne peut pas s'envoyer le
  // message à lui-même, mais tous les autres comptes (y compris Superadmin)
  // restent des destinataires possibles. Les comptes supprimés sont masqués.
  const creatorsFiltres = useMemo(() => {
    const sansExpediteur = creators.filter(c => c.id !== authorId && c.accountStatus !== 'deleted');
    if (recherche.trim() === '') return sansExpediteur;
    const q = recherche.trim().toUpperCase();
    // Chaque mot saisi doit se retrouver quelque part dans la fiche, ce qui
    // permet « prénom nom », « nom prénom » ou « nom email ».
    const mots = q.split(/\s+/);
    return sansExpediteur.filter(c => {
      const fiche = `${c.firstname} ${c.lastname} ${c.lastname} ${c.firstname} ${c.pseudonym} ${c.email}`.toUpperCase();
      return mots.every(m => fiche.includes(m));
    });
  }, [creators, recherche, authorId]);

  const chargerCreateurs = async () => {
    if (creators.length > 0 || creatorsLoading) return;
    setCreatorsLoading(true);
    setCreatorsErreur(null);
    try {
      const result = await getAdmins();
      setCreators(result.payload ?? []);
    } catch {
      setCreatorsErreur("Impossible de charger la liste des créateurs");
    } finally {
      setCreatorsLoading(false);
    }
  };

  const handleAudienceChange = (next: Audience) => {
    setAudience(next);
    if (next === 'chosen') chargerCreateurs();
  };

  const chargerEnvoyes = async () => {
    if (authorId === null) return;
    setSentLoading(true);
    setSentErreur(null);
    try {
      const result = await fetchSentCommunityMessages(authorId);
      setSentMessages(result);
    } catch {
      setSentErreur("Impossible de charger l'historique des envois");
    } finally {
      setSentLoading(false);
    }
  };

  const changerVue = (next: Vue) => {
    setVue(next);
    setOpenSentId(null);
    setExpandedId(null);
    if (next === 'envoyes') chargerEnvoyes();
  };

  const toggleOpenSent = (id: number) => {
    setOpenSentId(prev => (prev === id ? null : id));
  };

  const toggleDetail = async (id: number) => {
    if (expandedId === id) { setExpandedId(null); return; }
    setExpandedId(id);
    // Le détail se rafraîchit à chaque ouverture : le taux de lecture bouge
    // tant que des destinataires n'ont pas encore cliqué « J'ai lu ».
    setReadsLoadingId(id);
    try {
      const result = await fetchCommunityMessageReads(id);
      setReadsById(prev => ({ ...prev, [id]: result }));
    } catch {
      // Le badge affiché reste correct (compteurs déjà connus) ; seul le
      // détail par nom manque, sans conséquence bloquante.
    } finally {
      setReadsLoadingId(null);
    }
  };

  const handleRetract = async (id: number) => {
    setRetractingId(id);
    setSentErreur(null);
    try {
      await retractCommunityMessage(id);
      await chargerEnvoyes();
    } catch (err: any) {
      setSentErreur(err.message);
    } finally {
      setRetractingId(null);
    }
  };

  const toggleCreator = (id: number) => {
    setSelectedIds(prev => {
      const suivant = new Set(prev);
      if (suivant.has(id)) suivant.delete(id); else suivant.add(id);
      return suivant;
    });
  };

  const toutCocher = () => {
    setSelectedIds(new Set(creatorsFiltres.map(c => c.id)));
  };

  const toutDecocher = () => {
    setSelectedIds(new Set());
  };

  const fermer = () => {
    setOpen(false);
    setVue('ecrire');
    setTitre('');
    setTexte('');
    setEcheance('');
    setPrioritaire(false);
    setSurConnexion(false);
    setErreur(null);
    setEnvoye(false);
    setAudience('all');
    setSelectedIds(new Set());
    setRecherche('');
    setExpandedId(null);
  };

  const peutEnvoyer = titre.trim() !== '' && texte.trim() !== '' && !(audience === 'chosen' && selectedIds.size === 0);

  const handleSend = async () => {
    const messageTitre = titre.trim();
    const message = texte.trim();
    if (messageTitre === '' || message === '') return;
    if (audience === 'chosen' && selectedIds.size === 0) return;

    // Saisie libre (jj/mm/aaaa hh:mm) : une date illisible bloque l'envoi plutôt que de
    // publier un message qui ne serait jamais retiré, contre l'intention de son auteur.
    // Un prioritaire n'a pas d'échéance : une date saisie avant de cocher la case
    // ne compte plus.
    const saisieEcheance = prioritaire ? '' : echeance.trim();
    const echeanceDate = saisieEcheance !== '' ? convertStringToDate(saisieEcheance, 'dd/MM/yyyy HH:mm') : null;
    if (saisieEcheance !== '' && echeanceDate === null) {
      setErreur("La date de retrait n'est pas valide (format : jj/mm/aaaa hh:mm).");
      return;
    }

    setSending(true);
    setErreur(null);
    try {
      await sendCommunityMessage({
        authorId,
        authorName,
        title: messageTitre,
        message,
        // L'échéance est saisie en heure locale, sans fuseau ; la convertir ici
        // évite que le serveur la lise en UTC et retranche deux heures à
        // l'échéance sans que personne ne comprenne pourquoi.
        endDate: echeanceDate ? echeanceDate.toISOString() : null,
        recipientIds: audience === 'chosen' ? Array.from(selectedIds) : null,
        priority: prioritaire,
        showOnLogin: audience === 'all' && surConnexion,
      });
      setEnvoye(true);
      onSent();
    } catch (err: any) {
      setErreur(err.message);
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        style={{
          display        : 'flex',
          alignItems     : 'center',
          gap            : '8px',
          padding        : '12px 16px',
          backgroundColor: '#4c1d95',
          color          : 'white',
          border         : 'none',
          borderRadius   : '8px',
          boxShadow      : '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
          cursor         : 'pointer',
          fontWeight     : '500',
          fontSize       : '14px',
        }}
        title="Écrire à toute Kleon.TV"
      >
        Message
      </button>

      {open && (
        <Box onClose={fermer} width="520px" marginTop="0px" contentPadding="1.5rem">

          <p style={{ fontWeight: 600, fontSize: '18px', margin: '0 0 16px 0' }}>
            Message à Kleon.TV
          </p>

          {!envoye && (
            <div style={{ display: 'flex', gap: '4px', margin: '0 0 16px 0', borderBottom: '1px solid #e5e7eb' }}>
              {(['ecrire', 'envoyes'] as Vue[]).map(v => (
                <button
                  key     = {v}
                  onClick = {() => changerVue(v)}
                  style={{
                    padding      : '8px 4px',
                    marginBottom : '-1px',
                    border       : 'none',
                    borderBottom : vue === v ? '2px solid #7c3aed' : '2px solid transparent',
                    background   : 'none',
                    cursor       : 'pointer',
                    fontSize     : '13px',
                    fontWeight   : vue === v ? 600 : 400,
                    color        : vue === v ? '#7c3aed' : '#ffffff',
                  }}
                >
                  {v === 'ecrire' ? 'Écrire' : 'Envoyés'}
                </button>
              ))}
            </div>
          )}

          {vue === 'envoyes' && !envoye ? (
            <>
              {sentLoading && (
                <p style={{ fontSize: '13px', color: '#ffffff' }}>Chargement…</p>
              )}
              {sentErreur && (
                <p style={{ fontSize: '13px', color: '#b91c1c' }}>{sentErreur}</p>
              )}
              {!sentLoading && !sentErreur && sentMessages.length === 0 && (
                <p style={{ fontSize: '13px', color: '#ffffff' }}>Aucun message envoyé</p>
              )}

              {/* `paddingRight` : sans lui l'ascenseur vient se coller contre le badge
                  et le sous-bloc « qui a lu » quand celui-ci est déroulé. */}
              <div style={{ maxHeight: '420px', overflowY: 'auto', paddingRight: '10px' }}>
                {sentMessages.map(m => {
                  const ratio    = m.recipientCount > 0 ? m.readCount / m.recipientCount : 0;
                  const unSeul   = m.recipientCount === 1;
                  const detail   = readsById[m.id];
                  const estOuvert = expandedId === m.id;
                  const estDeplie = openSentId === m.id;

                  return (
                    <div key={m.id} style={{ padding: '12px 0', borderBottom: '1px solid #f3f4f6' }}>
                      {/* Compacté par défaut : titre à gauche, date/heure et
                          statut de retrait à droite, comme dans le panneau des
                          messages reçus (CommunityMessageManager) — même langue
                          visuelle. */}
                      <div
                        onClick={() => toggleOpenSent(m.id)}
                        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '8px', cursor: 'pointer' }}
                      >
                        <span style={{
                          fontSize      : '13px',
                          fontWeight    : 600,
                          color         : m.retracted ? '#ffffff' : '#ffffff',
                          textDecoration: m.retracted ? 'line-through' : 'none',
                        }}>
                          {m.priority === true && (
                            <span style={{
                              display        : 'inline-block',
                              marginRight    : '6px',
                              padding        : '1px 6px',
                              borderRadius   : '4px',
                              backgroundColor: ROUGE_PRIORITAIRE,
                              color          : 'white',
                              fontSize       : '10px',
                              fontWeight     : 700,
                              letterSpacing  : '0.5px',
                            }}>
                              PRIORITAIRE
                            </span>
                          )}
                          {m.title}
                        </span>

                        <span style={{ display: 'flex', alignItems: 'baseline', gap: '8px', flexShrink: 0 }}>
                          <span style={{ fontSize: '11px', color: '#ffffff' }}>{formatDateEnvoi(m.creationDate)}</span>

                          {/* Un seul des deux, jamais les deux à la fois : soit c'est
                              retiré (déjà dit), soit ça peut encore l'être.
                              `stopPropagation` : cliquer « Retirer » ne doit pas aussi
                              ouvrir/fermer le bloc. */}
                          {m.retracted ? (
                            <span style={{ fontSize: '11px', color: '#ffffff' }}>Retiré</span>
                          ) : (
                            <button
                              onClick  = {e => { e.stopPropagation(); handleRetract(m.id); }}
                              disabled = {retractingId === m.id}
                              style={{
                                padding        : '2px 8px',
                                fontSize       : '11px',
                                borderRadius   : '6px',
                                border         : '1px solid #ef4444',
                                cursor         : retractingId === m.id ? 'default' : 'pointer',
                                backgroundColor: 'transparent',
                                color          : '#ef4444',
                              }}
                              title="Retirer ce message de tous les écrans"
                            >
                              {retractingId === m.id ? 'Retrait…' : 'Retirer'}
                            </button>
                          )}
                        </span>
                      </div>

                      {estDeplie && (
                      <>
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px', alignItems: 'flex-start', marginTop: '8px' }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{
                            fontSize  : '13px',
                            // Le fond de la fenêtre est sombre (--color-Panel-niv1-BG) :
                            // un texte foncé y devient illisible.
                            color     : m.retracted ? '#ffffff' : '#ffffff',
                            whiteSpace: 'pre-wrap',
                            wordBreak : 'break-word',
                            textDecoration: m.retracted ? 'line-through' : 'none',
                          }}>
                            {m.message}
                          </div>
                        </div>

                        <button
                          onClick = {() => toggleDetail(m.id)}
                          title   = "Voir qui a lu"
                          style={{
                            flexShrink     : 0,
                            padding        : '4px 10px',
                            borderRadius   : '999px',
                            border         : 'none',
                            cursor         : 'pointer',
                            fontSize       : '11px',
                            fontWeight     : 600,
                            color          : 'white',
                            backgroundColor: unSeul
                              ? (m.readCount > 0 ? '#16a34a' : '#9ca3af')
                              : couleurJauge(ratio),
                          }}
                        >
                          {unSeul ? (m.readCount > 0 ? 'Lu' : 'Non lu') : `${m.readCount}/${m.recipientCount} lus`}
                        </button>
                      </div>
                      </>
                      )}

                      {estDeplie && estOuvert && (
                        <div style={{
                          marginTop   : '8px',
                          border      : '1px solid #e5e7eb',
                          borderRadius: '6px',
                        }}>
                          {readsLoadingId === m.id && !detail && (
                            <p style={{ fontSize: '12px', color: '#ffffff', padding: '8px 12px', margin: 0 }}>Chargement…</p>
                          )}
                          {detail && (
                            // `paddingRight` : sans lui l'ascenseur vient se coller contre
                            // les noms, demandé explicitement après une première version trop serrée.
                            <div style={{ maxHeight: '96px', overflowY: 'auto', paddingRight: '8px' }}>
                              {detail.map(d => (
                                <div
                                  key={d.adminId}
                                  style={{
                                    display   : 'flex',
                                    alignItems: 'center',
                                    gap       : '8px',
                                    height    : '32px',
                                    boxSizing : 'border-box',
                                    padding   : '0 12px',
                                    fontSize  : '12px',
                                  }}
                                >
                                  <span style={{
                                    width          : '8px',
                                    height         : '8px',
                                    borderRadius   : '50%',
                                    flexShrink     : 0,
                                    backgroundColor: d.read ? '#16a34a' : '#9ca3af',
                                  }} />
                                  <span style={{ flex: 1, color: '#ffffff' }}>{d.name}</span>
                                  <span style={{ color: '#ffffff' }}>{d.read ? 'Lu' : 'Non lu'}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          ) : vue === 'ecrire' && !envoye ? (
            <>
              <p style={{ fontSize: '13px', color: '#ffffff', margin: '0 0 12px 0' }}>
                Il apparaîtra en haut de l'écran des destinataires déjà connectés, et de
                ceux qui se connecteront tant qu'il n'est pas retiré.
              </p>

              <div style={{ display: 'flex', gap: '16px', margin: '0 0 12px 0', fontSize: '13px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                  <input
                    type    = "radio"
                    checked = {audience === 'all'}
                    onChange= {() => handleAudienceChange('all')}
                  />
                  Toute la communauté
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                  <input
                    type    = "radio"
                    checked = {audience === 'chosen'}
                    onChange= {() => handleAudienceChange('chosen')}
                  />
                  Créateurs choisis
                </label>
              </div>

              {audience === 'chosen' && (
                <div style={{
                  border      : '1px solid #d1d5db',
                  borderRadius: '6px',
                  marginBottom: '12px',
                  overflow    : 'hidden',
                }}>
                  <div style={{
                    display        : 'flex',
                    alignItems     : 'center',
                    gap            : '8px',
                    padding        : '8px',
                    borderBottom   : '1px solid #e5e7eb',
                    backgroundColor: 'rgba(255,255,255,0.06)',
                  }}>
                    <div style={{ flex: 1 }}>
                      <Field placeholder = "Rechercher un créateur…"
                             value       = {recherche}
                             onChange    = {e => setRecherche(e.target.value)} />
                    </div>
                    <button
                      type    = "button"
                      onClick = {toutCocher}
                      style   = {{ fontSize: '12px', color: '#7c3aed', background: 'none', border: 'none', cursor: 'pointer', whiteSpace: 'nowrap' }}
                    >
                      Tout cocher
                    </button>
                    <button
                      type    = "button"
                      onClick = {toutDecocher}
                      style   = {{ fontSize: '12px', color: '#ffffff', background: 'none', border: 'none', cursor: 'pointer', whiteSpace: 'nowrap' }}
                    >
                      Tout décocher
                    </button>
                  </div>

                  <div style={{ maxHeight: '96px', overflowY: 'auto', padding: '4px 0' }}>
                    {creatorsLoading && (
                      <p style={{ fontSize: '13px', color: '#ffffff', padding: '8px 12px', margin: 0 }}>Chargement…</p>
                    )}
                    {creatorsErreur && (
                      <p style={{ fontSize: '13px', color: '#b91c1c', padding: '8px 12px', margin: 0 }}>{creatorsErreur}</p>
                    )}
                    {!creatorsLoading && !creatorsErreur && creatorsFiltres.length === 0 && (
                      <p style={{ fontSize: '13px', color: '#ffffff', padding: '8px 12px', margin: 0 }}>Aucun créateur</p>
                    )}
                    {creatorsFiltres.map(c => (
                      <label
                        key   = {c.id}
                        style = {{
                          display   : 'flex',
                          alignItems: 'center',
                          gap       : '8px',
                          height    : '32px',
                          boxSizing : 'border-box',
                          padding   : '0 12px',
                          fontSize  : '13px',
                          cursor    : 'pointer',
                        }}
                      >
                        <input
                          type    = "checkbox"
                          checked = {selectedIds.has(c.id)}
                          onChange= {() => toggleCreator(c.id)}
                        />
                        <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {`${c.firstname} ${c.lastname}`.trim() || c.pseudonym || c.email}
                        </span>
                        <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', opacity: 0.7 }}>
                          {c.email}
                        </span>
                      </label>
                    ))}
                  </div>

                  <div style={{
                    padding        : '6px 12px',
                    fontSize       : '12px',
                    color          : '#ffffff',
                    borderTop      : '1px solid #e5e7eb',
                    backgroundColor: 'rgba(255,255,255,0.06)',
                  }}>
                    {selectedIds.size} créateur{selectedIds.size > 1 ? 's' : ''} sélectionné{selectedIds.size > 1 ? 's' : ''}
                  </div>
                </div>
              )}

              <div style={{ marginBottom: '8px' }}>
                <Field placeholder = "Titre du message…"
                       value       = {titre}
                       maxLength   = {LONGUEUR_TITRE_MAX}
                       onChange    = {e => setTitre(e.target.value.slice(0, LONGUEUR_TITRE_MAX))} />
              </div>

              <TextArea placeholder = "Votre message…"
                        value       = {texte}
                        maxLength   = {LONGUEUR_MAX}
                        height      = "150px"
                        onChange    = {e => setTexte(e.target.value.slice(0, LONGUEUR_MAX))} />

              <div style={{
                display       : 'flex',
                justifyContent: 'space-between',
                alignItems    : 'center',
                fontSize      : '12px',
                color         : '#ffffff',
                marginTop     : '4px',
              }}>
                {prioritaire ? (
                  <span>Reste affiché jusqu'à ce que vous le retiriez.</span>
                ) : (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span>Retirer automatiquement le</span>
                  <FieldCalendar value      = {echeance}
                                 onChange   = {e => setEcheance(e.target.value)}
                                 showHours  = {true}
                                 checkInput = {false}
                                 width      = "170px"
                                 placeholder= "jj/mm/aaaa hh:mm"
                                 dateFormat = "dd/MM/yyyy HH:mm" />
                </div>
                )}
                <span>{texte.length} / {LONGUEUR_MAX}</span>
              </div>

              {/* Le type « Prioritaire » : rouge dans la régie, et repris par les
                  apps Kleon Studio au-dessus de « Créer un direct ». */}
              <label style={{
                display        : 'flex',
                alignItems     : 'flex-start',
                gap            : '8px',
                marginTop      : '12px',
                padding        : '10px 12px',
                borderRadius   : '6px',
                border         : `1px solid ${prioritaire ? ROUGE_PRIORITAIRE : 'rgba(255,255,255,0.25)'}`,
                backgroundColor: prioritaire ? 'rgba(185,28,28,0.25)' : 'transparent',
                fontSize       : '13px',
                cursor         : 'pointer',
              }}>
                <input
                  type    = "checkbox"
                  checked = {prioritaire}
                  onChange= {() => setPrioritaire(p => !p)}
                  style   = {{ marginTop: '2px' }}
                />
                <span>
                  <strong>Prioritaire</strong>
                  <span style={{ display: 'block', fontSize: '12px', opacity: 0.85, marginTop: '2px' }}>
                    En rouge dans la régie, et affiché aussi dans les apps Kleon Studio
                    (iPhone et Android), au-dessus de « Créer un direct ». Reste en place
                    jusqu'à son retrait, même lu.
                  </span>
                </span>
              </label>

              {/* Page de connexion : lisible par tout visiteur, donc réservé à un
                  message adressé à toute la communauté. */}
              {audience === 'all' && (
                <label style={{
                  display        : 'flex',
                  alignItems     : 'flex-start',
                  gap            : '8px',
                  marginTop      : '12px',
                  padding        : '10px 12px',
                  borderRadius   : '6px',
                  border         : `1px solid ${surConnexion ? '#17c2e6' : 'rgba(255,255,255,0.25)'}`,
                  backgroundColor: surConnexion ? 'rgba(23,194,230,0.15)' : 'transparent',
                  fontSize       : '13px',
                  cursor         : 'pointer',
                }}>
                  <input
                    type    = "checkbox"
                    checked = {surConnexion}
                    onChange= {() => setSurConnexion(v => !v)}
                    style   = {{ marginTop: '2px' }}
                  />
                  <span>
                    <strong>Afficher sur la page de connexion</strong>
                    <span style={{ display: 'block', fontSize: '12px', opacity: 0.85, marginTop: '2px' }}>
                      Sous le panneau de connexion de la régie, visible de tout visiteur
                      avant même qu'il se connecte. À réserver aux annonces publiques.
                    </span>
                  </span>
                </label>
              )}

              {erreur && (
                <p style={{ color: '#b91c1c', fontSize: '13px', marginTop: '12px' }}>{erreur}</p>
              )}

              <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '20px' }}>
                <Trigger name="Annuler" onClick={fermer} variant="tailwind2"/>
                <Trigger
                  name                       = {sending ? 'Envoi…' : 'Envoyer'}
                  onClick                    = {handleSend}
                  variant                    = "tailwind2"
                  unavailable                = {sending || titre.trim() === '' || texte.trim() === '' || (audience === 'chosen' && selectedIds.size === 0)}
                  buttonForegroundColor      = {peutEnvoyer ? '#FFFFFF' : undefined}
                  buttonBackgroundColor      = {peutEnvoyer ? (prioritaire ? ROUGE_PRIORITAIRE : '#7c3aed') : undefined}
                  buttonForegroundHoverColor = {peutEnvoyer ? '#FFFFFF' : undefined}
                  buttonBackgroundHoverColor = {peutEnvoyer ? (prioritaire ? '#991b1b' : '#6d28d9') : undefined}
                />
              </div>
            </>
          ) : (
            <div style={{ textAlign: 'center' }}>
              <p style={{ fontWeight: 600, margin: '0 0 8px 0' }}>Message envoyé</p>
              <p style={{ fontSize: '13px', color: '#ffffff', margin: '0 0 20px 0' }}>
                Les postes déjà ouverts l'afficheront dans les trente secondes
                {prioritaire ? ", et les apps Kleon Studio dans la minute." : "."}
              </p>
              <Trigger name="Fermer" onClick={fermer} variant="tailwind2"/>
            </div>
          )}

        </Box>
      )}
    </>
  );
};

export default CommunityMessageComposer;
